import { makeAutoObservable } from 'mobx';
import {DomainStore} from 'shared/store';
import {t, api} from 'shared/core';
import {auth, types, endpoints} from 'main/tools';
import {Announcement} from 'main/stores/announcements';
import _ from 'lodash';
import {openFileStack, successAlert} from 'shared/tools';

class FeedState {
  store = new DomainStore();
  emojiReady;

  editAnnouncementModalOpen = false;
  editingAnnouncement;
  deletingAnnouncement;
  errors = {};
  selectedEmployees = [];
  deleteAnnouncementModalOpen = false;
  celebrations = [];
  announcements = [];
  nextLink = endpoints.ANNOUNCEMENTS;
  loading = false;
  loadingAnnouncementDetails = false;

  constructor() {
    makeAutoObservable(this, {
      store: false,
      emojiReady: false
    });
  }

  receiveProps({celebrations, emojiReady}) {
    this.celebrations = celebrations;
    this.emojiReady = emojiReady;
  }

  loadMore = async () => {
    this.loading = true;
    const response = await api.get(this.nextLink);
    this.store._repository.sync(response.data);
    this.nextLink = _.get(response, 'data.links.next');
    this.announcements = this.store._getAll(types.ANNOUNCEMENT, Announcement);
    this.loading = false;
  };

  async loadAnnouncementDetails(announcement) {
    this.loadingAnnouncementDetails = true;
    await this.store._compose([
      endpoints.ANNOUNCEMENT.with(announcement.id)
    ]);

    this.editingAnnouncement = new Announcement(this.store._getSingle(types.ANNOUNCEMENT, {id: announcement.id}));
    this.loadingAnnouncementDetails = false;
  }

  openEditAnnouncementModal(announcement) {
    this.editAnnouncementModalOpen = true;
    this.loadAnnouncementDetails(announcement);
  }

  openNewAnnouncementModal() {
    this.editingAnnouncement = new Announcement();
    this.editAnnouncementModalOpen = true;
  }

  closeEditAnnouncementModal() {
    this.editAnnouncementModalOpen = false;
    this.selectedEmployees = [];
    this.errors = {};
  }

  updateDescription(description) {
    this.editingAnnouncement.description = description.html;
    this.editingAnnouncement.lexicalState = description.state;
  }

  updateSelectedEmployees(employees) {
    this.selectedEmployees.replace(employees);
  }

  async saveAnnouncement() {
    if (this.editingAnnouncement.isNew) {
      await this.publishAnnouncement();
    } else {
      await this.updateAnnouncement();
    }
  }

  async updateAnnouncement() {
    const {model, errors} = await this.store.patch(this.editingAnnouncement);
    this.errors = errors;

    if (model) {
      _.find(this.announcements, {id: this.editingAnnouncement.id}).update(model);
      successAlert(t('hr_dashboard.feed.announcements.Announcement successfully saved'));
      this.closeEditAnnouncementModal();
    }
  }

  async openFileStack() {
    openFileStack({
      storeTo: {
        location: 's3',
        path: '/avatars/'
      }
    }, (file) => {
      this.editingAnnouncement.image = file;
    });
  }

  async clearImage() {
    this.editingAnnouncement.image = null;
  }

  async publishAnnouncement() {
    const {model, errors} = await this.store.post(
      endpoints.ANNOUNCEMENTS,
      types.ANNOUNCEMENT,
      {
        ...this.editingAnnouncement,
        employees: this.selectedEmployees
      }
    );
    this.errors = errors;

    if (model) {
      this.announcements.push(new Announcement(model));
      successAlert(t('hr_dashboard.feed.announcements.Announcement successfully published'));
      this.closeEditAnnouncementModal();
    }
  }

  async pinAnnouncement(announcement) {
    const {model} = await this.store.patch(announcement.link('pin'), types.ANNOUNCEMENT);

    if (model) {
      announcement.update(model);
      successAlert(t('hr_dashboard.feed.announcements.Announcement pinned'));
    }
  }

  async unpinAnnouncement(announcement) {
    const {model} = await this.store.patch(announcement.link('unpin'), types.ANNOUNCEMENT);

    if (model) {
      announcement.update(model);
      successAlert(t('hr_dashboard.feed.announcements.Announcement unpinned'));
    }
  }

  openDeleteAnnouncementModal(announcement) {
    this.deletingAnnouncement = announcement;
    this.deleteAnnouncementModalOpen = true;
  }

  closeDeleteAnnouncementModal() {
    this.deleteAnnouncementModalOpen = false;
  }

  async confirmAnnouncementDelete() {
    await this.store.destroy(this.deletingAnnouncement);

    _.remove(this.announcements, (announcement) => {
      return announcement.id === this.deletingAnnouncement.id;
    });
    successAlert(t('hr_dashboard.feed.announcements.Deleted announcement.'));
    this.closeDeleteAnnouncementModal();
  }

  get feedItems() {
    return this.celebrations.concat(this.announcements.slice());
  }

  get showCreateAnnouncementButton() {
    return auth.hasAccess('::MANAGE_ANNOUNCEMENTS') || auth.company.employeeAnnouncementsEnabled;
  }
}

export default FeedState;
